import FalconCardHeader from 'components/common/FalconCardHeader';
import { useAuth } from 'context/AuthContext';
import { updateUser } from 'helpers/access';
import {
  getUserPreferences,
  getUserProfileData,
  saveUserPreferences
} from 'helpers/userProfile';
import React, { useMemo, useState } from 'react';
import { Button, Card, Col, Form, Row } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

const ProfileSettings = () => {
  const { t } = useTranslation();
  const { userData, refresh } = useAuth();
  const profileData = getUserProfileData(userData);
  const preferences = useMemo(
    () => getUserPreferences(userData?.userId),
    [userData?.userId]
  );

  const [formData, setFormData] = useState({
    username: profileData.username || '',
    first_name: profileData.firstName || '',
    last_name: profileData.lastName || '',
    email: profileData.email || '',
    phone: preferences.phone || '',
    heading: preferences.heading || '',
    intro: preferences.intro || ''
  });
  const [isSaving, setIsSaving] = useState(false);

  const handleChange = event => {
    setFormData(currentState => ({
      ...currentState,
      [event.target.name]: event.target.value
    }));
  };

  const handleSubmit = async event => {
    event.preventDefault();
    setIsSaving(true);

    try {
      const membershipId = userData?.membership?.id;

      if (membershipId) {
        await updateUser(membershipId, {
          username: formData.username,
          email: formData.email,
          first_name: formData.first_name,
          last_name: formData.last_name
        });
      }

      saveUserPreferences(userData?.userId, {
        phone: formData.phone,
        heading: formData.heading,
        intro: formData.intro
      });

      refresh({
        userName: formData.username,
        userEmail: formData.email,
        user: {
          ...userData.user,
          username: formData.username,
          email: formData.email,
          first_name: formData.first_name,
          last_name: formData.last_name
        },
        membership: {
          ...userData.membership,
          username: formData.username,
          email: formData.email,
          first_name: formData.first_name,
          last_name: formData.last_name
        }
      });

      toast.success(t('USER_SETTINGS.TOASTS.PROFILE_SAVED'), {
        theme: 'colored'
      });
    } catch (requestError) {
      console.error(requestError);
      toast.error(
        requestError.response?.data?.error || t('USER_SETTINGS.ERRORS.PROFILE_SAVE'),
        { theme: 'colored' }
      );
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Card className="mb-3">
      <FalconCardHeader title={t('USER_SETTINGS.PROFILE.TITLE')} />
      <Card.Body className="bg-light">
        <p className="text-600 mb-4">{t('USER_SETTINGS.PROFILE.DESCRIPTION')}</p>
        <Form onSubmit={handleSubmit}>
          <Row className="mb-3 g-3">
            <Form.Group as={Col} lg={6} controlId="username">
              <Form.Label>{t('USER_SETTINGS.FIELDS.USERNAME')}</Form.Label>
              <Form.Control
                type="text"
                name="username"
                value={formData.username}
                onChange={handleChange}
                disabled
              />
              <Form.Text className="text-muted">
                {t('USER_SETTINGS.READ_ONLY.USERNAME')}
              </Form.Text>
            </Form.Group>

            <Form.Group as={Col} lg={6} controlId="email">
              <Form.Label>{t('USER_SETTINGS.FIELDS.EMAIL')}</Form.Label>
              <Form.Control
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                disabled
              />
              <Form.Text className="text-muted">
                {t('USER_SETTINGS.READ_ONLY.EMAIL')}
              </Form.Text>
            </Form.Group>
          </Row>

          <Row className="mb-3 g-3">
            <Form.Group as={Col} lg={6} controlId="first_name">
              <Form.Label>{t('USER_SETTINGS.FIELDS.FIRST_NAME')}</Form.Label>
              <Form.Control
                type="text"
                name="first_name"
                value={formData.first_name}
                onChange={handleChange}
              />
            </Form.Group>

            <Form.Group as={Col} lg={6} controlId="last_name">
              <Form.Label>{t('USER_SETTINGS.FIELDS.LAST_NAME')}</Form.Label>
              <Form.Control
                type="text"
                name="last_name"
                value={formData.last_name}
                onChange={handleChange}
              />
            </Form.Group>
          </Row>

          <Row className="mb-3 g-3">
            <Form.Group as={Col} lg={6} controlId="phone">
              <Form.Label>{t('USER_SETTINGS.FIELDS.PHONE')}</Form.Label>
              <Form.Control
                type="text"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                placeholder={t('USER_SETTINGS.PLACEHOLDERS.PHONE')}
              />
            </Form.Group>

            <Form.Group as={Col} lg={6} controlId="heading">
              <Form.Label>{t('USER_SETTINGS.FIELDS.HEADING')}</Form.Label>
              <Form.Control
                type="text"
                name="heading"
                value={formData.heading}
                onChange={handleChange}
                placeholder={t('USER_SETTINGS.PLACEHOLDERS.HEADING')}
              />
            </Form.Group>
          </Row>

          <Form.Group className="mb-3" controlId="intro">
            <Form.Label>{t('USER_SETTINGS.FIELDS.INTRO')}</Form.Label>
            <Form.Control
              as="textarea"
              rows={5}
              name="intro"
              value={formData.intro}
              onChange={handleChange}
              placeholder={t('USER_SETTINGS.PLACEHOLDERS.INTRO')}
            />
          </Form.Group>

          <div className="text-end">
            <Button variant="primary" type="submit" disabled={isSaving}>
              {isSaving
                ? t('USER_SETTINGS.ACTIONS.SAVING')
                : t('USER_SETTINGS.ACTIONS.SAVE_PROFILE')}
            </Button>
          </div>
        </Form>
      </Card.Body>
    </Card>
  );
};

export default ProfileSettings;
